/**
 * Shared page-building class recipes, so the pages can't drift apart on the basics.
 * Keep these in sync with the design language rather than copying the strings around.
 */

/** On a phone a page-level card steps out over the gutter and drops its side border, radius and shadow. */
export const cardBleedClass = 'max-sm:-mx-4 max-sm:rounded-none max-sm:border-x-0 max-sm:shadow-none';

/** The card surface without padding, for cards whose sections set their own. */
export const cardShellClass = `rounded-2xl border border-border bg-card shadow-sm ${cardBleedClass}`;

/** Canonical page-level content card: the white surface detail pages and reports are built from. */
export const cardClass = `${cardShellClass} p-6 max-md:p-4`;

/** The page's side padding; a card's phone padding matches it, so a bleeding row lines up either way. */
export const pageGutterClass = 'px-4 md:px-6';

/** Scrolls sideways itself so the page never does; `relative`, or an absolute child escapes the clip. */
export const scrollRowClass = 'no-scrollbar relative overflow-x-auto overflow-y-hidden max-md:-mx-4 max-md:px-4';

/**
 * Page title row height. Actions differ per page and per tab — a 40px sprint toggler on one tab, a 28px
 * "Add" button on the next — and without a floor the row is only as tall as whatever it holds, so the
 * title and everything under it shifted when you switched tabs. Pinned to the tallest action so every
 * page header is the same height and nothing moves.
 */
export const pageHeaderRowClass = 'min-h-10';

/**
 * The page heading, on every page that has one. Sized a step below the display scale: the heading only
 * has to be the first thing you read, and these pages carry tabs, filters and a table under it.
 */
export const pageTitleClass = 'text-2xl font-bold leading-none tracking-tight text-foreground';

/**
 * Canonical underline tab, used by every tab bar (page-level section tabs and the
 * By team / By day switches inside report cards).
 */
export const tabClass = (active: boolean): string =>
  `border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
    active ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;

/** An underline tab bar's row: tabs keep one line and their width here, and it scrolls when they outgrow it. */
export const tabBarClass = `flex gap-6 border-b border-border *:shrink-0 *:whitespace-nowrap ${scrollRowClass}`;

/**
 * A padless card's header: title and hint, with its controls beside them. When the controls would squeeze
 * the title (a phone, a half-width card) they drop to a row of their own under it instead.
 */
export const cardHeaderClass = 'flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-5 pt-4 pb-3 max-md:px-4';

/** The title block inside `cardHeaderClass`: it claims the width that decides whether the controls wrap. */
export const cardHeaderTitleClass = 'min-w-0 flex-[1_1_14rem]';

/**
 * A table's frame inside a padless card, inset like the card's text; a wide table scrolls inside it.
 */
export const tableFrameClass = 'relative mx-5 overflow-x-auto rounded-xl border border-border max-md:mx-4';

/** A sprint report's sheet: roomier than a card on a wide screen, and edge to edge like one on a phone. */
export const reportCardClass = `flex flex-col rounded-[10px] border border-border bg-card p-4 md:px-10 md:pt-10 md:pb-15 ${cardBleedClass}`;

/**
 * A dialog's own row of buttons, for a form that cannot use `DialogFooter`: on a phone it pins to the foot
 * of the sheet (see dialog.tsx) while the fields scroll behind it. Only as the dialog's last row.
 */
export const sheetFooterClass =
  'max-sm:sticky max-sm:bottom-[calc(-1rem-env(safe-area-inset-bottom))] max-sm:z-10 max-sm:-mx-4 max-sm:-mb-[calc(1rem+env(safe-area-inset-bottom))] max-sm:border-t max-sm:border-border max-sm:bg-popover max-sm:p-4 max-sm:pb-[calc(1rem+env(safe-area-inset-bottom))]';

/**
 * A list page's filters. On a phone each dropdown takes the row, the dates share one, and the yes/no
 * checkboxes sit side by side; from `sm` up everything wraps along one line, bottoms aligned.
 */
export const filterBarClass = 'flex flex-wrap items-end gap-x-4 gap-y-3 sm:gap-4';
